/**
 * _reactSelectStyles.js
 *
 * Shared react-select `styles` prop factory used by:
 *   - AppSelect (combobox mode, classNamePrefix="app-select-combobox")
 *   - AppCombobox              (classNamePrefix="app-combobox")
 *   - AppMultiSelect           (classNamePrefix="app-multiselect")
 *
 * Design tokens are read at render-time via CSS custom properties so they
 * respond to theme changes without a re-build.
 *
 * Green fallback is #00875A (brand green) — NOT the legacy #00e676.
 */

// ── Token helpers ─────────────────────────────────────────────────────────────
// These are used as fallback strings inside var() expressions.
// They match colorConstant.scss / Radix theme tokens.
const T = {
  border: "#CBD5E1", // --border-default
  borderSubtle: "#e2e8f0", // --border-subtle-2
  surface: "#ffffff", // --surface-card / --primary_white
  surfacePage: "#F1F5F9", // --surface-page (hover bg for options)
  surfaceThead: "#f1f5f9", // --surface-thead (multi-value chip bg)
  textBody: "#334155", // --text-body / --slate
  textMuted: "#94a3b8", // --text-muted-2
  textPlaceholder: "#94a3b8", // same token, placeholder context
  grayMedium: "#94a3b8", // --gray-medium (multiselect placeholder)
  grayDark: "#8e94a1", // --gray-dark (indicators)
  primary: "#00875A", // --primary_color  ← normalized green
  primaryDark: "#006644", // --green-active (selected+focused)
  greenTint: "rgba(0, 135, 90, 0.08)", // --green-tint-light
  shadow: "0 4px 16px rgba(0, 0, 0, 0.12)", // --shadow-dropdown
}

/**
 * buildReactSelectStyles
 *
 * @param {object} options
 * @param {boolean} [options.isMulti=false]   — enables multi-value chip styles
 * @param {"field"|"drop-field"} [options.variant] — "drop-field": bottom-border only, transparent bg
 * @returns {object} react-select `styles` prop object
 */
export function buildReactSelectStyles({ isMulti = false, variant } = {}) {
  const isDropField = variant === "drop-field"

  return {
    // ── menuPortal — keeps menu above modals/dialogs ────────────────────────
    menuPortal: (base) => ({ ...base, zIndex: 1060 }),

    // ── control ─────────────────────────────────────────────────────────────
    control: (base, state) => ({
      ...base,
      minHeight: 36,
      border: isDropField
        ? "none"
        : `1px solid var(--border-subtle-2, ${T.borderSubtle})`,
      borderBottom: isDropField
        ? `1px solid var(--border-subtle-2, ${T.borderSubtle})`
        : undefined,
      borderRadius: isDropField ? 0 : "var(--radius-md, 8px)",
      background: isDropField
        ? "transparent"
        : `var(--surface-card, ${T.surface})`,
      boxShadow:
        state.isFocused && !isDropField
          ? `0 0 0 3px var(--green-tint-light, ${T.greenTint})`
          : "none",
      // hover handled via CSS class — react-select applies &:hover only via className
      borderColor: state.isFocused
        ? `var(--primary_color, ${T.primary})`
        : undefined,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      opacity: state.isDisabled ? 0.5 : 1,
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      // Override react-select's blue focused border with our green
      "&:hover": {
        borderColor: `var(--primary_color, ${T.primary})`,
        borderBottom: isDropField
          ? `1px solid var(--primary_color, ${T.primary})`
          : undefined,
      },
    }),

    // ── valueContainer ──────────────────────────────────────────────────────
    valueContainer: (base) => ({
      ...base,
      padding: isMulti ? "2px 8px" : "2px 10px",
      gap: isMulti ? 4 : undefined,
    }),

    // ── singleValue ─────────────────────────────────────────────────────────
    singleValue: (base) => ({
      ...base,
      color: `var(--text-body, ${T.textBody})`,
      fontSize: "0.875rem",
    }),

    // ── placeholder ─────────────────────────────────────────────────────────
    placeholder: (base) => ({
      ...base,
      color: isMulti
        ? `var(--gray-medium, ${T.grayMedium})`
        : `var(--text-muted-2, ${T.textPlaceholder})`,
      fontSize: "0.875rem",
    }),

    // ── input ────────────────────────────────────────────────────────────────
    input: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: `var(--text-body, ${T.textBody})`,
      margin: 0,
      padding: 0,
    }),

    // ── indicatorSeparator ───────────────────────────────────────────────────
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: `var(--border-subtle-2, ${T.borderSubtle})`,
    }),

    // ── dropdownIndicator ────────────────────────────────────────────────────
    dropdownIndicator: (base, state) => ({
      ...base,
      color: `var(--gray-dark, ${T.grayDark})`,
      padding: 6,
      "&:hover": {
        color: `var(--text-body, ${T.textBody})`,
      },
    }),

    // ── clearIndicator ───────────────────────────────────────────────────────
    clearIndicator: (base) => ({
      ...base,
      color: `var(--gray-dark, ${T.grayDark})`,
      padding: 6,
      "&:hover": {
        color: `var(--text-body, ${T.textBody})`,
      },
    }),

    // ── loadingIndicator ────────────────────────────────────────────────────
    loadingIndicator: (base) => ({
      ...base,
      color: `var(--primary_color, ${T.primary})`,
    }),

    // ── menu ─────────────────────────────────────────────────────────────────
    menu: (base) => ({
      ...base,
      border: `1px solid var(--border-subtle-2, ${T.borderSubtle})`,
      borderRadius: "var(--radius-md, 8px)",
      boxShadow: `var(--shadow-dropdown, ${T.shadow})`,
      background: `var(--surface-card, ${T.surface})`,
      zIndex: 1060,
    }),

    // ── menuList ─────────────────────────────────────────────────────────────
    menuList: (base) => ({
      ...base,
      padding: 4,
      maxHeight: 300,
      "@media (max-width: 480px)": {
        maxHeight: 200,
      },
    }),

    // ── option ───────────────────────────────────────────────────────────────
    option: (base, state) => {
      let background
      if (state.isSelected && state.isFocused) {
        background = `var(--green-active, ${T.primaryDark})`
      } else if (state.isSelected) {
        background = `var(--primary_color, ${T.primary})`
      } else if (state.isFocused) {
        background = `var(--surface-page, ${T.surfacePage})`
      } else {
        background = "transparent"
      }

      return {
        ...base,
        padding: "8px 12px",
        fontSize: "0.875rem",
        borderRadius: "var(--radius-sm, 4px)",
        cursor: "pointer",
        color: state.isSelected ? "#fff" : `var(--text-body, ${T.textBody})`,
        background,
      }
    },

    // ── noOptionsMessage ─────────────────────────────────────────────────────
    noOptionsMessage: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: `var(--text-muted-2, ${T.textMuted})`,
      padding: "8px 12px",
    }),

    // ── loadingMessage ───────────────────────────────────────────────────────
    loadingMessage: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: `var(--text-muted-2, ${T.textMuted})`,
      padding: "8px 12px",
    }),

    // ── Multi-value chip ── only active when isMulti=true ────────────────────
    multiValue: (base) => ({
      ...base,
      background: `var(--surface-thead, ${T.surfaceThead})`,
      border: `1px solid var(--border-subtle-2, ${T.borderSubtle})`,
      borderRadius: "var(--radius-pill, 16px)",
      margin: 2,
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: `var(--slate, ${T.textBody})`,
      fontSize: "0.78rem",
      fontWeight: 500,
      padding: "2px 6px",
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: `var(--gray-dark, ${T.grayDark})`,
      borderRadius: `0 var(--radius-pill, 16px) var(--radius-pill, 16px) 0`,
      padding: "2px 4px",
      cursor: "pointer",
      "&:hover": {
        color: `var(--slate, ${T.textBody})`,
        background: "transparent",
      },
    }),
  }
}
